/**
 * The admin website for Kai for Teams. Owners and admins manage people, access,
 * groups and tracks; group leads see their part of the company and can give
 * tracks and reminders there. Everything comes from /v1/team and /v1/workspaces.
 */
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "./style.css";
import { isSignedIn, onSignedOut, redeemLink, signOut } from "./api.ts";
import { setRerender } from "./rerender.ts";
import { isAdmin, loadOverview, loadWorkspaces, pickWorkspace, rememberedWorkspace, state } from "./state.ts";
import { append, clear, errorText, go, h, parseRoute, type Route } from "./ui.ts";
import { renderActivity } from "./views/activity.ts";
import { renderGroups } from "./views/groups.ts";
import { renderOverview } from "./views/overview.ts";
import { renderPeople } from "./views/people.ts";
import { renderPerson } from "./views/person.ts";
import { renderSignIn } from "./views/signin.ts";
import { renderTracks } from "./views/tracks.ts";

const app = document.getElementById("app")!;

const NAV: { route: Route["name"]; label: string; adminOnly: boolean }[] = [
  { route: "overview", label: "Training", adminOnly: false },
  { route: "people", label: "People and access", adminOnly: true },
  { route: "groups", label: "Groups and leads", adminOnly: true },
  { route: "tracks", label: "Tracks", adminOnly: false },
  { route: "activity", label: "Activity", adminOnly: true },
];

function shell(route: Route): HTMLElement {
  const main = h("main", { id: "main", tabindex: -1 });
  const nav = h("nav", { "aria-label": "Sections" });
  const active = route.name === "person" ? "overview" : route.name;
  for (const n of NAV) {
    if (n.adminOnly && !isAdmin()) continue;
    nav.append(h("a", { href: `#/${n.route}`, "aria-current": n.route === active ? "page" : null }, n.label));
  }
  const picker =
    state.workspaces.length > 1
      ? (() => {
          const sel = h("select", { "aria-label": "Company" }) as HTMLSelectElement;
          for (const w of state.workspaces) sel.append(h("option", { value: w.id }, w.name));
          sel.value = state.ws!.id;
          sel.addEventListener("change", () => {
            pickWorkspace(sel.value);
            go("overview");
            void draw();
          });
          return sel;
        })()
      : h("p", { class: "company" }, state.ws!.name);
  const side = h(
    "aside",
    null,
    h("div", { class: "brand" }, h("span", { class: "mark", "aria-hidden": "true" }), "Kai for Teams"),
    picker,
    h("p", { class: "muted small role" }, isAdmin() ? (state.ws!.role === "owner" ? "Owner" : "Admin") : "Group lead"),
    nav,
    h(
      "div",
      { class: "me" },
      h("span", { class: "muted small" }, state.email),
      h("button", { type: "button", class: "small", onclick: () => signOut() }, "Sign out"),
    ),
  );
  app.append(h("div", { class: "frame" }, side, main));
  return main;
}

let drawing = 0;

async function draw() {
  const ticket = ++drawing;
  clear(app);
  if (!isSignedIn()) {
    const box = h("div", { class: "solo" });
    app.append(box);
    renderSignIn(box, () => void start());
    return;
  }
  if (!state.ws) {
    const box = h("div", { class: "solo" });
    app.append(box);
    append(
      box,
      h(
        "section",
        { class: "signin" },
        h("h1", null, "No team to show"),
        h("p", null, `${state.email || "This account"} isn't an owner, admin or group lead in a company workspace.`),
        h("p", { class: "muted" }, "Ask your company's ForEach admin to make you a lead, or sign in with your work email."),
        h("button", { type: "button", onclick: () => signOut() }, "Sign in with another email"),
      ),
    );
    return;
  }
  let route = parseRoute(location.hash);
  const entry = NAV.find((n) => n.route === route.name);
  if (entry?.adminOnly && !isAdmin()) route = { name: "overview" };
  const main = shell(route);
  main.append(h("p", { class: "muted loading" }, "Loading…"));
  try {
    const page = h("div", { class: "page" });
    if (route.name === "overview") await renderOverview(page);
    else {
      // Every screen knows the caller's own id and scope from the overview.
      await loadOverview();
      if (route.name === "person") await renderPerson(page, route.id);
      if (route.name === "people") await renderPeople(page);
      if (route.name === "groups") await renderGroups(page);
      if (route.name === "tracks") await renderTracks(page);
      if (route.name === "activity") await renderActivity(page);
    }
    if (ticket !== drawing) return;
    clear(main);
    main.append(page);
  } catch (e) {
    if (ticket !== drawing) return;
    clear(main);
    main.append(h("div", { class: "page" }, h("h1", null, "That didn't load"), h("p", { class: "late" }, errorText(e)), h("button", { type: "button", onclick: () => void draw() }, "Try again")));
  }
}

async function start(preferred: string | null = null) {
  if (!isSignedIn()) return draw();
  try {
    await loadWorkspaces();
    pickWorkspace(preferred ?? rememberedWorkspace());
  } catch (e) {
    clear(app);
    const box = h("div", { class: "solo" });
    app.append(box);
    box.append(h("section", { class: "signin" }, h("h1", null, "Can't reach ForEach"), h("p", { class: "late" }, errorText(e)), h("button", { type: "button", onclick: () => void start() }, "Try again")));
    return;
  }
  await draw();
}

setRerender(() => void draw());
onSignedOut(() => {
  state.workspaces = [];
  state.ws = null;
  state.overview = null;
  void draw();
});
window.addEventListener("hashchange", () => void draw());

(async () => {
  let preferred: string | null = null;
  if (/code=/.test(location.hash)) {
    const hash = location.hash;
    history.replaceState(null, "", `${location.pathname}#/overview`);
    try {
      preferred = (await redeemLink(hash)).workspaceId;
    } catch {
      clear(app);
      const box = h("div", { class: "solo" });
      app.append(box);
      renderSignIn(box, () => void start(), "That link has expired. Sign in with your email instead.");
      return;
    }
  }
  await start(preferred);
})();
